/**
 * @quotech/ui - Universal Dashboard Layout Component
 *
 * ALL MODULES MUST USE THIS LAYOUT
 *
 * Features:
 * - Combines Sidebar + Header + Content area
 * - Handles responsive behavior (mobile drawer)
 * - Manages command palette (Cmd+K)
 * - Theme management
 * - Sidebar collapse state
 * - Hub switching via HubSwitcher
 * - Job context for job-centric navigation
 */

import { useMemo, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useRenderTiming } from '../diagnostics/useRenderTiming'
import { useScrollToTopOnRouteChange } from './dashboard-layout/useScrollToTopOnRouteChange'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { CommandPalette } from './CommandPalette'
import { MobileDrawer } from './MobileDrawer'
import { SettingsModal } from './SettingsModal'
import { GlobalSearch } from './GlobalSearch'
import { createNavSearchProvider } from './global-search/providers/navigation'
import { useExtraGlobalSearch } from './global-search/extraProviders'
import { defaultQuickActions, type SearchProvider } from './global-search/types'
import { NotificationCenter, useNotifications } from './NotificationCenter'
import { LiveToastStack } from '../live-toasts/LiveToastStack'
import { useNotificationToasts } from '../live-toasts/useNotificationToasts'
import { AIContextSidebar } from './AIContextSidebar'
import { UniversalDock, type DockAction, type DockPanel } from './UniversalDock'
import { DockActionsProvider, useDockActions } from '../hooks/useDockActions'
import { DockPanelsProvider, useDockPanels } from '../hooks/useDockPanels'
import { cn } from '../lib/utils'
import { MobileDock } from './mobile-dock/MobileDock'
import { toMobileDockItems } from './mobile-dock/toMobileDockItems'
import { useLayoutState } from './dashboard-layout/useLayoutState'
import { GlobalShortcutBindings } from './dashboard-layout/GlobalShortcutBindings'
import { ShortcutProvider } from '../shortcuts/ShortcutProvider'
import { useShortcutOverridesRuntime } from '../shortcuts/useShortcutOverridesRuntime'
import type { DashboardLayoutProps } from './dashboard-layout/types'

export type { DashboardLayoutProps }

export function DashboardLayout(props: DashboardLayoutProps) {
  const {
    children, module, currentHub = 'jobs',
    navItems, secondaryNavItems, externalLinks, onHubClick,
    quickAccessItems, pinnedItems, hiddenItems, onUnhideItem,
    resolveNavContext, onNavItemClick, sidebarSectionLabels,
    breadcrumbs, showOrgSwitcher = false, organisations = [],
    currentOrganisationId, onSelectOrganisation, onOrgClick,
    showNotifications = false, notificationCount: propNotificationCount = 0, notifications: propNotifications = [],
    onNotificationsClick, onNotificationNavigate, notificationApiClient, notificationToastResolver,
    showHubSwitcher = true, pinnedHubs = [],
    quickAddItems = [], showQuickAdd = true, jobContext, onClearJobContext,
    onOpenCustomize, showCommandPalette = true, commands = [], hubLinks = [],
    user, onLogout, onUseProfile, contentClassName, noContentPadding = false, className, headerRightActionsSlot,
    mobileNavHost = false, quietMode = false, hideHeaderSearch = false, themeInUserMenu = false,
    hideLockedSettingsTabs = false, showSettingsModal = false, settingsTabs = [],
    defaultSettingsTab = 'profile', onOpenSettings, settingsModalOpen: externalSettingsOpen,
    onCloseSettings, showGlobalSearch = true, globalSearchApiClient,
    globalSearchLocalJobs = [], searchProviders = [], navSearchExtraSources, hubsInSearch = true,
    globalSearchContextFilters,
    showAISidebar = false, aiContext, tabBarSlot, headerTabsSlot,
    showBottomDock = true, bottomDockActions = [], bottomDockPanels = [], hideBottomDock = false,
    sidebarActionSlot, accessibleModules,
    sidebarHubId, sidebarDefaultCollapsed,
    autoCollapseRoutePrefixes, currentPathname,
    autoCollapseBelowXl = true,
    hideDesktopSidebar = false,
    hideMobileHubDock = false,
  } = props

  // Cross-tenant app-shell render metric, re-measured per navigation. Feeds
  // the 0x3admin Performance dashboard's "render p95 per UI element" (no-op
  // until the perf reporter is installed by installClientErrorReporter).
  const _routeLocation = useLocation()
  useRenderTiming('AppShell', { deps: [_routeLocation.pathname] })

  // Scroll the content area back to the top on every route navigation.
  // The `<main>` below is the persistent shell's scroll container
  // (`overflow-y-auto`) and is NEVER remounted; React Router swaps the
  // routed children in place but leaves its scrollTop untouched, so a
  // menu/sidebar click used to land mid-page at the previous view's
  // offset (operator report 2026-06-18). Keyed on pathname so in-page
  // query-param changes don't disturb the operator's scroll.
  const mainContentRef = useRef<HTMLElement>(null)
  useScrollToTopOnRouteChange(mainContentRef, _routeLocation.pathname)

  const filteredExternalLinks = useMemo(() => {
    if (!externalLinks || !accessibleModules || accessibleModules.length === 0) return externalLinks
    return externalLinks.filter(link => {
      if (!link.id) return true
      if (link.id === 'jobs') return true
      return accessibleModules.includes(link.id)
    })
  }, [externalLinks, accessibleModules])

  const mobileDockItems = useMemo(
    () => toMobileDockItems(navItems, secondaryNavItems, filteredExternalLinks),
    [navItems, secondaryNavItems, filteredExternalLinks],
  )

  const extraSearch = useExtraGlobalSearch()

  // Default search provider built from the host's own nav.
  //
  // Why this exists in the layout, not in each app: the user
  // reported "search doesn't even work here" across multiple
  // apps. Rolling `createNavSearchProvider` into 13 separate
  // App.tsx files would invite drift and forgetting; instead
  // every app that mounts `<DashboardLayout navItems=…/>` now
  // gets ranked nav search-as-you-type for free.
  //
  // Merge semantics (BUG-2026-05-25-MASTER-GLOBAL-SEARCH-NO-
  // KANBAN-OR-EMAIL): if the host passes `searchProviders`, we
  // ALWAYS MERGE them with the nav fallback rather than replace.
  // Previously the explicit list "won" — hosts that wired a
  // kanban provider silently lost nav search and operators
  // couldn't navigate from Cmd+K anymore. The host's providers
  // come FIRST in the merged list so module hits surface above
  // generic page links (e.g. a `Hudson Pde tender` card beats
  // a hypothetical `Hudson` nav link); the nav provider is the
  // safety-net that guarantees the palette is never useless.
  // Hosts that genuinely want to suppress nav can pass an
  // explicit provider with id `navigation` (see dedup contract
  // below) — collision = host wins.
  const effectiveSearchProviders = useMemo<SearchProvider[]>(() => {
    // Strip incompatible fields (e.g. NavItem.icon is a React
    // component, while NavSearchSource.icon expects a string
    // name). The provider only needs `label`, `href`/`url`,
    // `description`, `keywords`, and the synthetic `group`.
    const toSource = (n: any, group: string) => ({
      id: n.id,
      label: n.label,
      description: n.description,
      keywords: n.keywords,
      href: n.href,
      url: (n as any).url,
      group,
    })
    // Rail-derived sources FIRST (so their group labels win), then any
    // host-supplied extra page catalog (pages NOT on the active rail —
    // e.g. master pages while inside a consolidated hub). Deduped by the
    // link target so the same destination never appears twice.
    const rawNavSources = [
      ...(navItems ?? []).map((n) =>
        toSource(n, sidebarSectionLabels?.main ?? 'Main'),
      ),
      ...(secondaryNavItems ?? []).map((n) =>
        toSource(n, sidebarSectionLabels?.secondary ?? 'Tools'),
      ),
      ...(filteredExternalLinks ?? []).map((n) =>
        toSource(n, sidebarSectionLabels?.hubs ?? 'Hubs'),
      ),
      ...(navSearchExtraSources ?? []),
    ].filter((n) => !!n.label && (!!n.href || !!n.url))
    const seenNavTargets = new Set<string>()
    const navSources = rawNavSources.filter((n) => {
      const key = (n.href ?? n.url) as string
      if (seenNavTargets.has(key)) return false
      seenNavTargets.add(key)
      return true
    })

    const hostProviders = searchProviders ?? []
    const hostIds = new Set(hostProviders.map((p) => p.id))
    const navProvider =
      navSources.length > 0 && !hostIds.has('navigation')
        ? [
            createNavSearchProvider(navSources, {
              id: 'navigation',
              label: 'Pages',
              // Float STRONG (exact/prefix) page matches to the top
              // `pages` section so a query that names a destination
              // ("schedule" → Schedule page) is the FIRST thing the
              // operator sees, above the on-board card tier. Without
              // this the nav hit is unsectioned and sinks to the very
              // bottom of the list (BUG-2026-06-23-SCHEDULE-NAV-BURIED:
              // operator searched "schedule" to jump to the Schedule
              // tab and the result was buried under the board's cards).
              // Mirrors AdvancedSearchPopup's nav provider so master +
              // popup behave identically. Only confident label matches
              // are tagged (see createNavSearchProvider) — fuzzy keyword
              // hits stay unsectioned and never bury content tiers.
              section: 'pages',
            }),
          ]
        : []

    return [...hostProviders, ...extraSearch.providers, ...navProvider]
  }, [
    searchProviders,
    extraSearch.providers,
    navItems,
    secondaryNavItems,
    filteredExternalLinks,
    navSearchExtraSources,
    sidebarSectionLabels,
  ])

  const state = useLayoutState({
    quietMode, showGlobalSearch, showCommandPalette, showSettingsModal,
    showAISidebar, externalSettingsOpen, onOpenSettings, onCloseSettings,
    sidebarHubId, sidebarDefaultCollapsed,
    autoCollapseRoutePrefixes, currentPathname,
    autoCollapseBelowXl,
  })

  // UNIVERSAL-SHORTCUTS PHASE 2a — the app shell now mounts the central
  // keyboard dispatcher. Overrides are read (not written) here from the same
  // persisted slice the settings editor writes, so a user's remap/disable
  // applies at dispatch time. The provider only acts on REGISTERED actions
  // (see GlobalShortcutBindings), so un-migrated chords are untouched.
  const shortcutOverrides = useShortcutOverridesRuntime()

  // #1237: the selected org also keys the workforce-api feed, so a manager's
  // submitted-timesheet alert shows in this bell, not only in the Diary.
  const notifHook = useNotifications(showNotifications ? notificationApiClient : undefined, {
    workforceOrgId: currentOrganisationId ?? null,
  })
  const notificationCount = notificationApiClient ? notifHook.unreadCount : propNotificationCount
  const notifications = notificationApiClient ? notifHook.notifications : propNotifications
  const liveToasts = Boolean(showNotifications && notificationApiClient)
  useNotificationToasts(notifHook.notifications, notificationToastResolver, liveToasts)

  const content = (
    <>
      {/* Global Search (Cmd+K)
       *
       * BUG-2026-05-27-KANBAN-SEARCH-NOT-BOARD-SCOPED — `initialQuery`
       * + `initialActiveFilters` are seeded by the
       * `quotech:open-global-search` listener inside `useLayoutState`.
       * They forward an in-context query (e.g. the in-board kanban
       * search term) into the palette and are cleared by
       * `handleCloseGlobalSearch` on dismiss so manual ⌘F starts
       * blank. */}
      {showGlobalSearch && (
        <GlobalSearch
          isOpen={state.globalSearchOpen}
          onClose={state.handleCloseGlobalSearch}
          apiClient={globalSearchApiClient}
          localJobs={globalSearchLocalJobs}
          searchProviders={effectiveSearchProviders}
          quickActions={[...defaultQuickActions, ...extraSearch.actions]}
          initialQuery={state.globalSearchInitialQuery}
          // Event-seeded scope wins; else the host's route-resolved context
          // scope (W2); else open wide (legacy behaviour).
          initialActiveFilters={state.globalSearchInitialActiveFilters ?? globalSearchContextFilters}
          pinnedHubs={hubsInSearch ? filteredExternalLinks : undefined}
          onPinnedHubClick={onHubClick}
          currentHubId={currentHub}
          panelMaxWidthClassName={hubsInSearch ? 'max-w-5xl' : undefined}
        />
      )}

      {/* Command Palette (fallback) */}
      {showCommandPalette && !showGlobalSearch && (
        <CommandPalette
          isOpen={state.commandPaletteOpen}
          onClose={() => state.setCommandPaletteOpen(false)}
          commands={commands}
          hubLinks={hubLinks}
          placeholder={`Search ${module.name}...`}
        />
      )}

      {liveToasts && <LiveToastStack onNavigate={onNotificationNavigate} />}

      {/* Notification Center overlay */}
      {showNotifications && state.notificationCenterOpen && (
        <NotificationCenter
          isOpen={state.notificationCenterOpen}
          onClose={() => state.setNotificationCenterOpen(false)}
          notifications={notifications as any}
          unreadCount={notificationCount}
          isLoading={notifHook.isLoading}
          onMarkAsRead={notifHook.markAsRead}
          onMarkAllAsRead={notifHook.markAllAsRead}
          onDismiss={notifHook.dismiss}
          onDismissAll={notifHook.dismissAll}
          onNavigate={onNotificationNavigate}
          hideTrigger
        />
      )}

      {/* 100dvh (not 100vh / h-screen): on mobile browsers + the Capacitor
          WebView, 100vh excludes the dynamic toolbar, making this column
          taller than the visible viewport while the fixed dock pins to the
          layout viewport — which exaggerated content clipping under the dock. */}
      <div className={cn('flex h-[100dvh] bg-background transition-colors', className)}>
        {/* Mobile Drawer */}
        <MobileDrawer
          isOpen={state.mobileDrawerOpen}
          onClose={() => { state.setMobileDrawerOpen(false); state.setDrawerOpenMode(null) }}
          openMode={state.drawerOpenMode}
        >
          <Sidebar
            navItems={navItems} secondaryNavItems={secondaryNavItems}
            externalLinks={filteredExternalLinks} quickAccessItems={quickAccessItems}
            hideExternalLinks={hubsInSearch}
            pinnedItems={pinnedItems} hiddenItems={hiddenItems} onUnhideItem={onUnhideItem}
            resolveNavContext={resolveNavContext}
            onNavItemClick={onNavItemClick}
            module={module} user={user} collapsed={false}
            onOpenSearch={state.handleOpenSearch} onLogout={onLogout}
            onOpenCustomize={onOpenCustomize} onHubClick={onHubClick}
            currentHubId={currentHub}
            sectionLabels={sidebarSectionLabels} actionSlot={sidebarActionSlot}
            className="border-r-0"
          />
        </MobileDrawer>

        {/* Desktop Sidebar (lg+ only) — hidden when a route opts into bottom-dock nav */}
        {!hideDesktopSidebar && (
        <div className="hidden lg:block flex-shrink-0">
          <Sidebar
            navItems={navItems} secondaryNavItems={secondaryNavItems}
            externalLinks={filteredExternalLinks} quickAccessItems={quickAccessItems}
            hideExternalLinks={hubsInSearch}
            pinnedItems={pinnedItems} hiddenItems={hiddenItems} onUnhideItem={onUnhideItem}
            resolveNavContext={resolveNavContext}
            onNavItemClick={onNavItemClick}
            module={module} user={user}
            collapsed={state.userCollapsed} onCollapsedChange={state.setUserCollapsed}
            onOpenSearch={state.handleOpenSearch} onLogout={onLogout}
            onOpenCustomize={onOpenCustomize} onHubClick={onHubClick}
            currentHubId={currentHub}
            sectionLabels={sidebarSectionLabels} actionSlot={sidebarActionSlot}
          />
        </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <Header
            module={module} breadcrumbs={breadcrumbs} user={user}
            theme={state.resolvedTheme} onToggleTheme={state.toggleTheme}
            onOpenSearch={state.handleOpenSearch}
            onOpenMobileSidebar={(mode: 'hover' | 'click' = 'click') => {
              state.setMobileDrawerOpen(true); state.setDrawerOpenMode(mode)
            }}
            showOrgSwitcher={showOrgSwitcher} organisations={organisations}
            currentOrganisationId={currentOrganisationId}
            onSelectOrganisation={onSelectOrganisation} onOrgClick={onOrgClick}
            showNotifications={showNotifications} notificationCount={notificationCount}
            onNotificationsClick={state.handleOpenNotifications}
            currentHub={currentHub} pinnedHubs={pinnedHubs}
            showHubSwitcher={showHubSwitcher} quickAddItems={quickAddItems}
            showQuickAdd={showQuickAdd} jobContext={jobContext}
            onClearJobContext={onClearJobContext} onOpenCustomize={onOpenCustomize}
            onLogout={onLogout} onUseProfile={onUseProfile}
            onOpenSettings={showSettingsModal ? state.openSettings : undefined}
            headerRightActionsSlot={headerRightActionsSlot}
            quietMode={quietMode} showAIButton={showAISidebar}
            onOpenAI={() => state.setAISidebarOpen(true)}
            hideMobileDockTriggers={hideBottomDock}
            hideSearch={hideHeaderSearch} themeInUserMenu={themeInUserMenu}
            tabsSlot={headerTabsSlot}
            mobileNavHost={mobileNavHost}
          />
          {tabBarSlot}
          {/* overflow-x stays hidden. overflow-y-auto would otherwise turn a
              specified overflow-x of visible into auto, so a wide schedule
              top bar scrolls this whole column and slides the grid. */}
          <main ref={mainContentRef} className={cn(
            'flex-1 min-h-0 overflow-x-hidden overflow-y-auto bg-background transition-colors',
            !noContentPadding && 'p-4 sm:p-6',
            contentClassName
          )}
          style={hideMobileHubDock ? undefined : {
            paddingBottom: 'max(1.5rem, calc(var(--mobile-dock-h, 0px) + 0.75rem))',
          }}
          >
            {children || <Outlet />}
          </main>
        </div>

        {/* AI Sidebar — only mounts when a context is supplied (the sidebar
            dereferences context.* immediately, so guarding also avoids a crash). */}
        {showAISidebar && aiContext && (
          <AIContextSidebar
            isOpen={state.aiSidebarOpen}
            onClose={() => state.setAISidebarOpen(false)}
            context={aiContext}
          />
        )}

        {/* Settings Modal */}
        {showSettingsModal && (
          <SettingsModal
            isOpen={state.settingsOpen}
            onClose={state.closeSettings}
            tabs={settingsTabs}
            defaultTab={state.settingsTab ?? defaultSettingsTab}
            user={user}
            hideLockedTabs={hideLockedSettingsTabs}
          />
        )}
      </div>

      {!hideMobileHubDock && (
        <MobileDock
          items={mobileDockItems}
          storageKey={`quotech.mobile-dock.${currentHub}`}
          usageHubId={currentHub}
        />
      )}

      {/* Universal Bottom Dock */}
      {showBottomDock && !hideBottomDock && (
        <DockWithContextSinks
          user={user}
          propActions={bottomDockActions}
          propPanels={bottomDockPanels}
        />
      )}
    </>
  )

  return (
    <ShortcutProvider overrides={shortcutOverrides}>
      <DockActionsProvider>
        <DockPanelsProvider>
          <GlobalShortcutBindings
            onOpenSettings={state.openSettings}
            settingsEnabled={showSettingsModal}
          />
          {content}
        </DockPanelsProvider>
      </DockActionsProvider>
    </ShortcutProvider>
  )
}

/**
 * Bridge: merges prop-declared actions+panels with context-injected
 * ones from child components. Both shapes are first-class:
 *   - Actions = one-shot click buttons (DockAction).
 *   - Panels  = toggle button + slide-out body (DockPanel, PR 3d).
 */
function DockWithContextSinks({
  user,
  propActions = [],
  propPanels = [],
}: {
  user?: any
  propActions?: DockAction[]
  propPanels?: DockPanel[]
}) {
  const { actions: contextActions } = useDockActions()
  const { panels: contextPanels } = useDockPanels()
  return (
    <UniversalDock
      user={user}
      customActions={[...contextActions, ...propActions]}
      customPanels={[...contextPanels, ...propPanels]}
    />
  )
}

export default DashboardLayout
